import { isoWeekOf, shiftMonth, shiftWeek, weekRange, type WeekKey } from './iso-week'

/**
 * Leitura do PMC (pmc.flexibase.com.br) — projetos e atividades de cada pessoa.
 *
 * O PMC mora no mesmo Supabase do dev01 (`public.pmc_*`) e usa o mesmo login:
 * `responsavel_id` da atividade é o `auth.users.id` do SSO, o mesmo de
 * `supply.member.user_id`. O Supply só lê; quem decide o que volta é o RLS do
 * PMC. Regras de contagem aqui, puras e testadas (`pmc.test.ts`).
 */

export const PMC_URL = 'https://pmc.flexibase.com.br'

export type PmcTaskStatus = 'todo' | 'in_progress' | 'awaiting_approval' | 'done' | 'cancelled' | 'late'

/** Os mesmos rótulos da tela do PMC. */
export const PMC_TASK_STATUS_LABEL: Record<PmcTaskStatus, string> = {
  todo: 'A fazer',
  in_progress: 'Em andamento',
  awaiting_approval: 'Aguardando aprovação',
  done: 'Concluída',
  cancelled: 'Cancelada',
  late: 'Atrasada',
}

/** Atividade do PMC já normalizada (a linha do PostgREST vira isto em `data/pmc.ts`). */
export interface PmcTask {
  readonly id: string
  readonly titulo: string
  readonly status: PmcTaskStatus
  readonly responsavelId: string | null
  readonly projetoId: string | null
  readonly projetoNome: string | null
  readonly projetoNumero: number | null
  /** `yyyy-MM-dd`. */
  readonly prazo: string | null
  /** Dia (`yyyy-MM-dd`) em que entrou no PMC — "recebida". */
  readonly criadaEm: string | null
  /** Dia da entrega: `entregue_em` (enviou para aprovação) ou, sem ele, `data_conclusao`. */
  readonly entregueEm: string | null
}

/** Link para a atividade: o PMC não tem rota de atividade, abre o projeto (como "Minhas atividades" do PMC). */
export function pmcTaskUrl(task: Pick<PmcTask, 'projetoId'>): string {
  return task.projetoId ? `${PMC_URL}/projects/${encodeURIComponent(task.projetoId)}` : `${PMC_URL}/my-activities`
}

/** Com a pessoa: a fazer, em andamento ou marcada atrasada. Aguardando aprovação já foi entregue. */
export function isPmcOpen(task: Pick<PmcTask, 'status'>): boolean {
  return task.status === 'todo' || task.status === 'in_progress' || task.status === 'late'
}

export function isPmcDelivered(task: Pick<PmcTask, 'status'>): boolean {
  return task.status === 'done' || task.status === 'awaiting_approval'
}

/** Aberta e vencida: status atrasado no PMC ou prazo antes de hoje. */
export function isPmcLate(task: Pick<PmcTask, 'status' | 'prazo'>, today: string): boolean {
  return isPmcOpen(task) && (task.status === 'late' || (task.prazo !== null && task.prazo < today))
}

/** Dia da entrega efetiva (para "no prazo"): a data registrada, ou nenhuma. */
function deliveredOn(task: PmcTask): string | null {
  return isPmcDelivered(task) ? task.entregueEm : null
}

export interface PmcPersonStats {
  /** Com a pessoa agora (sem as aguardando aprovação). */
  readonly abertas: number
  readonly atrasadas: number
  /** Abertas com prazo de hoje até domingo desta semana. */
  readonly aVencer: number
  readonly aguardandoAprovacao: number
  /** Atividades com prazo dentro da janela e já vencido (base das taxas). */
  readonly vencidasNaJanela: number
  readonly entreguesNoPrazo: number
  /** Entregues no prazo ÷ vencidas na janela; `null` sem base. */
  readonly taxaNoPrazo: number | null
  /** Atrasadas agora ÷ abertas; `null` sem abertas. */
  readonly taxaAtraso: number | null
}

/**
 * Régua por pessoa.
 *
 * - Taxa de entrega no prazo: das atividades com prazo entre `from` e ontem
 *   (já venceram), quantas foram entregues até o prazo. Entregue depois do
 *   prazo, ou ainda aberta, conta contra.
 * - Taxa de atraso: das abertas agora, quantas estão atrasadas.
 *
 * @param today `yyyy-MM-dd` (São Paulo). @param from início da janela das taxas.
 */
export function pmcPersonStats(tasks: readonly PmcTask[], today: string, from: string): PmcPersonStats {
  const live = tasks.filter((t) => t.status !== 'cancelled')
  const open = live.filter((t) => isPmcOpen(t))
  const late = open.filter((t) => isPmcLate(t, today))
  const sunday = weekRange(isoWeekOf(today)).sunday
  const due = live.filter((t) => t.prazo !== null && t.prazo >= from && t.prazo < today)
  const onTime = due.filter((t) => {
    const d = deliveredOn(t)
    return d !== null && d <= (t.prazo as string)
  })
  return {
    abertas: open.length,
    atrasadas: late.length,
    aVencer: open.filter((t) => !isPmcLate(t, today) && t.prazo !== null && t.prazo >= today && t.prazo <= sunday).length,
    aguardandoAprovacao: live.filter((t) => t.status === 'awaiting_approval').length,
    vencidasNaJanela: due.length,
    entreguesNoPrazo: onTime.length,
    taxaNoPrazo: due.length ? onTime.length / due.length : null,
    taxaAtraso: open.length ? late.length / open.length : null,
  }
}

/** Lista curta: atrasadas primeiro (mais antigas antes), depois pelo prazo; sem prazo no fim. */
export function prioritizePmcTasks(tasks: readonly PmcTask[], today: string): PmcTask[] {
  return tasks
    .filter((t) => isPmcOpen(t))
    .sort(
      (a, b) =>
        Number(isPmcLate(b, today)) - Number(isPmcLate(a, today)) ||
        (a.prazo ?? '9999-12-31').localeCompare(b.prazo ?? '9999-12-31') ||
        a.titulo.localeCompare(b.titulo, 'pt-BR'),
    )
}

export interface PmcFlowWeek {
  readonly week: WeekKey
  readonly recebidas: number
  readonly entregues: number
}

/** Fluxo semanal: recebidas (criadas) × entregues, nas `weeks` semanas até a de `today`. */
export function pmcFlow(tasks: readonly PmcTask[], today: string, weeks: number): PmcFlowWeek[] {
  const last = isoWeekOf(today)
  const list = Array.from({ length: weeks }, (_, i) => shiftWeek(last, i - weeks + 1))
  const index = new Map(list.map((w, i) => [w as string, i]))
  const out = list.map((week) => ({ week, recebidas: 0, entregues: 0 }))
  for (const t of tasks) {
    if (t.status === 'cancelled') continue
    if (t.criadaEm) {
      const i = index.get(isoWeekOf(t.criadaEm))
      if (i !== undefined) out[i]!.recebidas += 1
    }
    const d = deliveredOn(t)
    if (d) {
      const i = index.get(isoWeekOf(d))
      if (i !== undefined) out[i]!.entregues += 1
    }
  }
  return out
}

/** Primeiro dia da janela das taxas e do fluxo (`weeks` semanas, começando na segunda). */
export function pmcWindowStart(today: string, weeks: number): string {
  return weekRange(shiftWeek(isoWeekOf(today), -(weeks - 1))).monday
}

/** Projeto do PMC com as atividades abertas de um recorte (árvore da visão do gestor). */
export interface PmcProjectGroup {
  /** Id do projeto, ou `null` para atividades sem projeto. */
  readonly projetoId: string | null
  readonly nome: string
  readonly numero: number | null
  readonly abertas: number
  readonly atrasadas: number
  /** Abertas na ordem da lista curta (atrasadas primeiro). */
  readonly tasks: readonly PmcTask[]
}

/**
 * Atividades abertas agrupadas por projeto do PMC. Projetos com atrasadas
 * primeiro (mais atrasadas antes), depois pelo prazo mais próximo; sem
 * projeto no fim. Dentro do projeto, `prioritizePmcTasks`.
 */
export function groupPmcByProject(tasks: readonly PmcTask[], today: string): PmcProjectGroup[] {
  const byKey = new Map<string, PmcTask[]>()
  for (const t of prioritizePmcTasks(tasks, today)) {
    const k = t.projetoId ?? ''
    byKey.set(k, [...(byKey.get(k) ?? []), t])
  }
  const groups = [...byKey].map(([k, list]): PmcProjectGroup => {
    const first = list[0]!
    return {
      projetoId: k || null,
      nome: k ? first.projetoNome ?? 'Projeto do PMC' : 'Sem projeto',
      numero: k ? first.projetoNumero : null,
      abertas: list.length,
      atrasadas: list.filter((t) => isPmcLate(t, today)).length,
      tasks: list,
    }
  })
  const nextDue = (g: PmcProjectGroup) => g.tasks.find((t) => t.prazo)?.prazo ?? '9999-12-31'
  return groups.sort(
    (a, b) =>
      Number(a.projetoId === null) - Number(b.projetoId === null) ||
      b.atrasadas - a.atrasadas ||
      nextDue(a).localeCompare(nextDue(b)) ||
      a.nome.localeCompare(b.nome, 'pt-BR'),
  )
}

/** Meses (`yyyy-MM`) de janeiro do ano de `today` até o mês atual, sem meses futuros. */
export function pmcYearMonths(today: string): string[] {
  const last = today.slice(0, 7)
  const out: string[] = []
  for (let m = `${today.slice(0, 4)}-01`; m <= last; m = shiftMonth(m, 1)) out.push(m)
  return out
}

/** Entregas (enviadas para aprovação ou concluídas) por mês, nos `months` dados. */
export function pmcDeliveriesByMonth(tasks: readonly PmcTask[], months: readonly string[]): number[] {
  const index = new Map(months.map((m, i) => [m, i]))
  const out = months.map(() => 0)
  for (const t of tasks) {
    const d = t.status === 'cancelled' ? null : deliveredOn(t)
    const i = d ? index.get(d.slice(0, 7)) : undefined
    if (i !== undefined) out[i]! += 1
  }
  return out
}

/** Soma corrida (visão "Acumulado" das entregas). */
export function accumulate(values: readonly number[]): number[] {
  let sum = 0
  return values.map((v) => (sum += v))
}
